import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl, API_ENDPOINTS } from '../../config/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import './Analytics.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('24h');
  
  const navigate = useNavigate();

  const token = localStorage.getItem('token');

  const fetchAnalytics = useCallback(async () => {
    try {
      setError(null);
      const headers = {
        Authorization: `Bearer ${token}`
      };

      const analyticsRes = await axios.get(getApiUrl(`/api/admin/analytics?period=${selectedPeriod}`), { headers });
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setError('Failed to load analytics data. Please try again.');
      
      if (error.response?.status === 401) {
        localStorage.clear();
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate, selectedPeriod]);

  useEffect(() => {
    // Check if user is logged in
    if (!token) {
      navigate('/admin');
      return;
    }

    fetchAnalytics();
  }, [token, navigate, fetchAnalytics]);

  // Generate chart data for US States
  const generateStatesChartData = () => {
    if (!analytics?.locationStats?.byState || analytics.locationStats.byState.length === 0) {
      return null;
    }

    // Show all states, not just top 15
    const statesData = analytics.locationStats.byState.slice(0, 50);
    const labels = statesData.map(state => state.state);
    const data = statesData.map(state => state.count);

    return {
      labels,
      datasets: [
        {
          label: 'Visitors by State',
          data,
          backgroundColor: 'rgba(34, 211, 238, 0.8)',
          borderColor: 'rgba(34, 211, 238, 1)',
          borderWidth: 2,
          borderRadius: 6,
          borderSkipped: false,
          hoverBackgroundColor: 'rgba(59, 130, 246, 0.9)',
          hoverBorderColor: 'rgba(59, 130, 246, 1)',
        }
      ]
    };
  };

  const statesChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: 'US States - Visitor Distribution (All States)',
        color: '#22d3ee',
        font: {
          size: 18,
          weight: 'bold'
        },
        padding: {
          bottom: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        titleColor: '#22d3ee',
        bodyColor: '#f8fafc',
        borderColor: 'rgba(34, 211, 238, 0.5)',
        borderWidth: 2,
        cornerRadius: 10,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        },
        callbacks: {
          label: function(context) {
            return `${context.parsed.y} visitors`;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: {
          color: '#94a3b8',
          font: {
            size: 10
          },
          maxRotation: 90,
          minRotation: 45
        },
        grid: {
          color: 'rgba(59, 130, 246, 0.15)',
          borderColor: 'rgba(34, 211, 238, 0.3)',
          lineWidth: 1
        },
        title: {
          display: true,
          text: 'US States',
          color: '#22d3ee',
          font: {
            size: 14,
            weight: 'bold'
          }
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: '#94a3b8',
          font: {
            size: 12
          }
        },
        grid: {
          color: 'rgba(59, 130, 246, 0.15)',
          borderColor: 'rgba(34, 211, 238, 0.3)',
          lineWidth: 1
        },
        title: {
          display: true,
          text: 'Number of Visitors',
          color: '#22d3ee',
          font: {
            size: 14,
            weight: 'bold'
          }
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="analytics-loading">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-error">
        <div className="error-content">
          <h2>❌ Analytics Error</h2>
          <p>{error}</p>
          <button onClick={fetchAnalytics} className="retry-button">
            🔄 Try Again
          </button>
        </div>
      </div>
    );
  }

  const statesChartData = generateStatesChartData();

  return (
    <div className="analytics-container">
      {/* Analytics Header */}
      <div className="analytics-header">
        <h1 className="analytics-title">Site Analytics</h1>
        <div className="period-selector">
          <label>Time Period:</label>
          <select className="period-select" value={selectedPeriod} onChange={(e) => setSelectedPeriod(e.target.value)}>
            <optgroup label="Quick Select">
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
            </optgroup>
            <optgroup label="Specific Months">
              <option value="current-month">This Month</option>
              <option value="last-month">Last Month</option>
            </optgroup>
            <optgroup label="Yearly">
              <option value="current-year">This Year</option>
              <option value="last-year">Last Year</option>
            </optgroup>
          </select>
        </div>
      </div>



      {/* Visit Stats Grid */}
      <div className="visit-stats-grid">
        <div className="visit-stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <h4>Total Visits</h4>
            <div className="stat-value">{(analytics?.totalVisits?.current) || 0}</div>
            <div className="stat-change positive">
              +100% <span className="vs-previous">vs previous period</span>
            </div>
          </div>
        </div>
        <div className="visit-stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h4>Daily Visitors</h4>
            <div className="stat-value">{Math.round(((analytics?.totalVisits?.current) || 0) / 30)}</div>
            <div className="stat-change positive">
              +8.2% <span className="vs-previous">vs previous period</span>
            </div>
          </div>
        </div>
        <div className="visit-stat-card">
          <div className="stat-icon">📱</div>
          <div className="stat-content">
            <h4>Mobile Visits</h4>
            <div className="stat-value">{analytics?.marketingMetrics?.devices?.find(d => d.device === 'Mobile')?.count || 0}</div>
            <div className="stat-change positive">
              +12.1% <span className="vs-previous">vs previous period</span>
            </div>
          </div>
        </div>
        <div className="visit-stat-card">
          <div className="stat-icon">🖥️</div>
          <div className="stat-content">
            <h4>Desktop Visits</h4>
            <div className="stat-value">{analytics?.marketingMetrics?.devices?.find(d => d.device === 'Desktop')?.count || 0}</div>
            <div className="stat-change negative">
              -3.2% <span className="vs-previous">vs previous period</span>
            </div>
          </div>
        </div>
        <div className="visit-stat-card">
          <div className="stat-icon">⏱️</div>
          <div className="stat-content">
            <h4>Bounce Rate</h4>
            <div className="stat-value">{analytics?.marketingMetrics?.bounceRate || 0}%</div>
            <div className="stat-change positive">
              -5.3% <span className="vs-previous">vs previous period</span>
            </div>
          </div>
        </div>
      </div>

      {/* US States Bar Chart */}
      {statesChartData && (
        <div className="states-chart-section">
          <h2 className="section-title">🗺️ US States - Visitor Distribution</h2>
          <div className="chart-container">
            <Bar data={statesChartData} options={statesChartOptions} />
          </div>
        </div>
      )}

      {/* Pages Section */}
      <div className="pages-section">
        <h2 className="section-title">📄 Most Visited Pages (Excluding Admin)</h2>
        <div className="pages-list">
          {(analytics?.topPages && Array.isArray(analytics.topPages) && analytics.topPages.length > 0) ? (
            analytics.topPages.slice(0, 8).map((page, index) => (
              <div key={page.page || index} className="page-item">
                <div className="page-rank">{index + 1}</div>
                <div className="page-info">
                  <div className="page-name">{page.page || 'Unknown Page'}</div>
                  <div className="page-count">{page.count || 0} visits</div>
                </div>
                <div className="page-bar">
                  <div 
                    className="page-bar-fill" 
                    style={{ width: `${(page.count || 0) / (analytics?.topPages?.[0]?.count || 1) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))
          ) : (
            <div className="no-data">
              <p>No page data available</p>
            </div>
          )}
        </div>
      </div>

      {/* Location Section */}
      <div className="location-section">
        <h2 className="section-title">Visitor Locations</h2>
        <div className="location-grid">
          <div className="location-list">
            <h5>Top Countries</h5>
            {(analytics?.locationStats?.byCountry && Array.isArray(analytics.locationStats.byCountry) && analytics.locationStats.byCountry.length > 0) ? (
              analytics.locationStats.byCountry.slice(0, 5).map((location, index) => (
                <div key={location.country || index} className="location-item">
                  <div className="location-name">{location.country || 'Unknown'}</div>
                  <div className="location-count">{location.count || 0}</div>
                </div>
              ))
            ) : (
              <div className="no-data">No location data</div>
            )}
          </div>
          <div className="location-list">
            <h5>Top Cities</h5>
            {(analytics?.locationStats?.byCity && Array.isArray(analytics.locationStats.byCity) && analytics.locationStats.byCity.length > 0) ? (
              analytics.locationStats.byCity.slice(0, 5).map((city, index) => (
                <div key={city.city || index} className="location-item">
                  <div className="location-name">{city.city || 'Unknown'}</div>
                  <div className="location-count">{city.count || 0}</div>
                </div>
              ))
            ) : (
              <div className="no-data">No city data</div>
            )}
          </div>
        </div>
      </div>

      {/* Marketing Metrics */}
      <div className="marketing-metrics-section">
        <h2 className="section-title">Marketing Metrics</h2>
        <div className="marketing-metrics-grid">
          <div className="metric-card">
            <h5>Peak Hours</h5>
            <div className="peak-hours-list">
              {(analytics?.marketingMetrics?.peakHours && Array.isArray(analytics.marketingMetrics.peakHours)) ? (
                analytics.marketingMetrics.peakHours.map((hour, index) => (
                  <div key={hour.hour || index} className="peak-hour-item">
                    <div className="hour-rank">{index + 1}</div>
                    <div className="hour-time">{hour.time || `${hour.hour}:00`}</div>
                    <div className="hour-count">{hour.count || 0} visits</div>
                  </div>
                ))
              ) : (
                <div className="no-data">No peak hour data</div>
              )}
            </div>
          </div>

          <div className="metric-card">
            <h5>Top Browsers</h5>
            <div className="browser-list">
              {(analytics?.marketingMetrics?.browsers && Array.isArray(analytics.marketingMetrics.browsers)) ? (
                analytics.marketingMetrics.browsers.slice(0, 4).map((browser, index) => {
                  const totalBrowserVisits = analytics.marketingMetrics.browsers.reduce((sum, b) => sum + b.count, 0);
                  const percentage = totalBrowserVisits > 0 ? Math.round((browser.count / totalBrowserVisits) * 100) : 0;
                  return (
                    <div key={browser.browser || index} className="browser-item">
                      <div className="browser-name">{browser.browser}</div>
                      <div className="browser-bar">
                        <div className="browser-bar-fill" style={{ width: `${percentage}%` }}></div>
                      </div>
                      <div className="browser-count">{percentage}%</div>
                    </div>
                  );
                })
              ) : (
                <div className="no-data">No browser data</div>
              )}
            </div>
          </div>

          <div className="metric-card">
            <h5>Traffic Sources</h5>
            <div className="referrer-list">
              {(analytics?.marketingMetrics?.referrers && Array.isArray(analytics.marketingMetrics.referrers)) ? (
                analytics.marketingMetrics.referrers.slice(0, 4).map((referrer, index) => {
                  const totalReferrerVisits = analytics.marketingMetrics.referrers.reduce((sum, r) => sum + r.count, 0);
                  const percentage = totalReferrerVisits > 0 ? Math.round((referrer.count / totalReferrerVisits) * 100) : 0;
                  return (
                    <div key={referrer.referrer || index} className="referrer-item">
                      <div className="referrer-name">{referrer.referrer}</div>
                      <div className="referrer-bar">
                        <div className="referrer-bar-fill" style={{ width: `${percentage}%` }}></div>
                      </div>
                      <div className="referrer-count">{percentage}%</div>
                    </div>
                  );
                })
              ) : (
                <div className="no-data">No referrer data</div>
              )}
            </div>
          </div>

          <div className="metric-card">
            <h5>Conversion Funnel</h5>
            <div className="funnel-stats">
              <div className="funnel-step">
                <span className="funnel-label">Page Visits</span>
                <span className="funnel-value">{(analytics?.totalVisits?.current) || 0}</span>
              </div>
              <div className="funnel-step">
                <span className="funnel-label">Contact Form Views</span>
                <span className="funnel-value">{Math.round(((analytics?.totalVisits?.current) || 0) * 0.15)}</span>
              </div>
              <div className="funnel-step">
                <span className="funnel-label">Form Submissions</span>
                <span className="funnel-value">{Math.round(((analytics?.totalVisits?.current) || 0) * 0.03)}</span>
              </div>
              <div className="funnel-conversion">
                Conversion Rate: {analytics?.marketingMetrics?.conversionFunnel?.contactRate || 3.2}%
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics; 